import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  Button,
  Input,
} from '@cibli/shared';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { phoneRegex } from '@/features/register/hooks/usePhoneForm';

type PhoneInputModalProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (phone: string) => void;
  isLoading?: boolean;
};

const PhoneInputModal = ({ open, onOpenChange, onSubmit, isLoading }: PhoneInputModalProps) => {
  const { t } = useTranslation();
  const [phone, setPhone] = useState('');
  const [error, setError] = useState('');

  const validatePhone = useCallback(
    (value: string): boolean => {
      if (!value.trim()) {
        setError(t('PhoneInputModal.errorRequired'));
        return false;
      }
      if (!phoneRegex.test(value.trim().replace(/[\s.-]/g, ''))) {
        setError(t('PhoneInputModal.errorInvalid'));
        return false;
      }
      setError('');
      return true;
    },
    [t],
  );

  const handleSubmit = useCallback(() => {
    if (validatePhone(phone)) {
      onSubmit(phone.trim());
    }
  }, [phone, validatePhone, onSubmit]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' && !isLoading) {
        e.preventDefault();
        handleSubmit();
      }
    },
    [handleSubmit, isLoading],
  );

  const handleClose = useCallback(() => {
    setPhone('');
    setError('');
    onOpenChange(false);
  }, [onOpenChange]);

  return (
    <AlertDialog open={open} onOpenChange={handleClose}>
      <AlertDialogContent className="sm:max-w-xl">
        <AlertDialogHeader>
          <AlertDialogTitle className="text-2xl font-bold">
            {t('PhoneInputModal.title')}
          </AlertDialogTitle>
          <AlertDialogDescription className="text-lg">
            {t('PhoneInputModal.description')}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <div className="flex flex-col gap-4 py-4">
          <Input
            id="phone-input"
            type="tel"
            placeholder={t('PhoneInputModal.placeholder')}
            value={phone}
            onChange={e => {
              setPhone(e.target.value);
              if (error) setError('');
            }}
            onKeyDown={handleKeyDown}
            className="text-lg h-14 px-4"
            autoFocus
            maxLength={24}
            autoComplete="off"
            autoCorrect="off"
            spellCheck={false}
            aria-describedby={error ? 'phone-error' : undefined}
            aria-invalid={!!error}
            aria-label={t('PhoneInputModal.title')}
          />
          {error && (
            <p id="phone-error" role="alert" className="text-sm text-destructive">
              {error}
            </p>
          )}
        </div>
        <AlertDialogFooter className="gap-3">
          <Button variant="outline" size="lg" onClick={handleClose}>
            {t('common.cancel')}
          </Button>
          <Button size="lg" onClick={handleSubmit} disabled={isLoading}>
            {isLoading ? t('PhoneInputModal.sending') : t('PhoneInputModal.submit')}
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};

export default PhoneInputModal;
